/**
 * Templates tab — Wrangle-reference UI.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────┐
 *   │ [toolbar right]           [Manage Variables] [+ Create]│
 *   ├────────────────────────────────────────────────────────┤
 *   │ ┌────── template card ─────┐  ┌────── template ──────┐ │
 *   │ │ ✉  Subject line   ⋯      │  │ ✉  Subject        ⋯ │ │
 *   │ │                         │  │                      │ │
 *   │ │ Body preview with fade… │  │ Body preview…        │ │
 *   │ │                         │  │                      │ │
 *   │ │ 👤 Created by …          │  │ 👤 Created by …       │ │
 *   │ └─────────────────────────┘  └──────────────────────┘ │
 *   └────────────────────────────────────────────────────────┘
 *
 * Data model is unchanged — we still persist through
 * `emailTemplateService` (`ggj_b2b_email_templates`) with the same
 * `{ key, name, subject, body_html, variables }` shape so the outbox,
 * compose picker, and CTA/approve-deny-pass flows keep working.
 *
 * What changed surgically:
 *   - The user-facing form now shows only Subject + body; the stable
 *     `key` is auto-generated on create and preserved on edit.
 *   - Template body and variables are rendered in a centered modal
 *     (`TemplateEditorModal`) instead of an inline route page.
 *   - Org-level reusable variables are managed via
 *     `ManageVariablesPopover` (localStorage for now; see service
 *     migration note in `emailVariablesStore.ts`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MoreHorizontal, Pencil, Send, Trash2, User } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import {
  emailTemplateService,
  type EmailTemplate,
} from '@core/supabase/services/emailTemplateService';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { useToast } from '@shared/hooks/useToast';
import { TemplateEditorModal } from '../components/TemplateEditorModal';
import { StarterTemplatesSection } from '../components/StarterTemplatesSection';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Strips HTML so the card preview renders as readable plain text with line
 * breaks, matching the Wrangle card body preview style.
 */
function stripHtmlForPreview(html: string): string {
  if (!html) return '';
  const withBreaks = html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '• ');
  const doc =
    typeof window !== 'undefined'
      ? new DOMParser().parseFromString(withBreaks, 'text/html')
      : null;
  const text = doc ? doc.body.textContent ?? '' : withBreaks.replace(/<[^>]+>/g, '');
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

// ── Template card ────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: EmailTemplate;
  authorLabel: string | null;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onUseInEmail: () => void;
}

function TemplateCard({
  template,
  authorLabel,
  canEdit,
  onEdit,
  onDelete,
  onUseInEmail,
}: TemplateCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const preview = useMemo(
    () => stripHtmlForPreview(template.body_html),
    [template.body_html],
  );
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <div
      ref={rootRef}
      className="group relative flex w-full flex-col overflow-hidden rounded-[12px] border transition-colors hover:border-[color-mix(in_srgb,#10B981_45%,var(--vscode-border-primary))] hover:shadow-sm"
      style={{
        background: 'var(--vscode-bg-elevated)',
        borderColor: 'var(--vscode-border-primary)',
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onEdit}
        className="flex items-start gap-2 px-3.5 pt-3 pb-2 text-left"
      >
        <span
          className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[6px]"
          style={{
            background:
              'color-mix(in srgb, #10B981 14%, var(--vscode-bg-elevated))',
            color: '#10B981',
          }}
          aria-hidden
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6h16v12H4z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="m4 7 8 6 8-6"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <p
          className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-snug"
          style={{ color: 'var(--vscode-text-primary)' }}
          title={template.subject || template.name}
        >
          {template.subject || template.name || 'Untitled template'}
        </p>
      </button>

      {/* Kebab menu */}
      {canEdit && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-[5px] opacity-0 transition-opacity hover:bg-[var(--vscode-bg-hover)] group-hover:opacity-100 focus:opacity-100"
            aria-label="Template actions"
            aria-expanded={menuOpen}
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-2 top-8 z-20 w-[140px] overflow-hidden rounded-[8px] border shadow-lg"
              style={{
                background: 'var(--vscode-bg-elevated)',
                borderColor: 'var(--vscode-border-primary)',
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onUseInEmail();
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                style={{ color: '#10B981' }}
              >
                <Send className="h-3.5 w-3.5" />
                Use in Email
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ color: '#DC2626' }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          )}
        </>
      )}

      {/* Body preview with fade-out */}
      <button
        type="button"
        onClick={onEdit}
        className="relative block h-[110px] w-full overflow-hidden px-3.5 text-left"
      >
        <p
          className="whitespace-pre-wrap text-[11.5px] leading-[1.55]"
          style={{ color: 'var(--vscode-text-secondary)' }}
        >
          {preview || <span className="italic opacity-60">No body yet</span>}
        </p>
        {/* Fade */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
          style={{
            background:
              'linear-gradient(to bottom, transparent, var(--vscode-bg-elevated))',
          }}
        />
      </button>

      {/* Footer */}
      <div
        className="flex items-center gap-1.5 border-t px-3.5 py-2"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <User
          className="h-3 w-3"
          style={{ color: 'var(--vscode-text-tertiary)' }}
          aria-hidden
        />
        <span
          className="truncate text-[10.5px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Created by {authorLabel || 'you'}
        </span>
      </div>
    </div>
  );
}

// ── Main tab export ──────────────────────────────────────────────────────────

interface EmailTemplatesTabProps {
  /**
   * Increment this to force a re-fetch after an external save (e.g. from
   * the editor modal opened via the tab-strip button in EmailPage).
   */
  refreshKey?: number;
  /** Controls the editor modal when driven from EmailPage's tab-strip button. */
  editorOpen?: boolean;
  onEditorClose?: () => void;
  onSaved?: () => void;
  /**
   * Called when the user picks "Use in Email" on any template card or starter.
   * Receives the compose `templateSelect` value to pre-fill (`org:<id>` for org
   * templates, builtin key for starters after they are saved as org templates).
   */
  onUseInEmail?: (templateSelectValue: string) => void;
}

export function EmailTemplatesTab({
  refreshKey = 0,
  editorOpen: externalEditorOpen,
  onEditorClose,
  onSaved: onExternalSaved,
  onUseInEmail,
}: EmailTemplatesTabProps = {}) {
  const { user } = useAuth();
  const perms = usePermissions();
  const canEdit = perms.canSendEmail || perms.isAdmin || perms.isOwner;
  const { toast, showToast } = useToast();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // Internal editor state (used when card's edit button is clicked, or starter "Use")
  const [internalEditorOpen, setInternalEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [prefillSubject, setPrefillSubject] = useState('');
  const [prefillBody, setPrefillBody] = useState('');

  // Resolve whether the editor is open (external create OR internal card-edit)
  const isEditorOpen = externalEditorOpen === true || internalEditorOpen;
  const handleEditorClose = () => {
    setInternalEditorOpen(false);
    setEditing(null);
    setPrefillSubject('');
    setPrefillBody('');
    onEditorClose?.();
  };

  // Open editor pre-filled from a starter template
  const openFromStarter = (subject: string, body: string) => {
    setEditing(null);
    setPrefillSubject(subject);
    setPrefillBody(body);
    setInternalEditorOpen(true);
  };

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(() => {
    if (!user?.orgId) return;
    emailTemplateService
      .list(user.orgId)
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [user?.orgId]);

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    emailTemplateService
      .list(user.orgId)
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [user?.orgId, refreshKey]);

  const openEdit = (t: EmailTemplate) => {
    setEditing(t);
    setInternalEditorOpen(true);
  };

  const handleSaved = (saved: EmailTemplate) => {
    setTemplates((prev) => {
      const idx = prev.findIndex((t) => t.id === saved.id);
      if (idx === -1) return [saved, ...prev];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
    reload();
    onExternalSaved?.();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await emailTemplateService.delete(deleteTarget.id);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
      showToast({ title: 'Template deleted.' });
    } catch (err) {
      showToast({
        title: err instanceof Error ? err.message : 'Could not delete template.',
        kind: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (!user?.orgId) {
    return (
      <p className="py-8 text-center text-sm vscode-text-secondary">
        Select an organisation to manage email templates.
      </p>
    );
  }
  if (loading) return <FullPageSpinner />;

  return (
    <div className="relative flex flex-col">
      {toast}

      {/* Org templates grid — only shown when at least one exists */}
      {templates.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              authorLabel={t.created_by === user.id ? (user.email ?? 'you') : 'team'}
              canEdit={canEdit}
              onEdit={() => openEdit(t)}
              onDelete={() => setDeleteTarget(t)}
              onUseInEmail={() => onUseInEmail?.(`org:${t.id}`)}
            />
          ))}
        </div>
      )}

      {/* Starter templates — collapsible when org templates exist, expanded when empty */}
      <StarterTemplatesSection
        onEdit={openFromStarter}
        defaultExpanded={templates.length === 0}
        onUseInEmail={onUseInEmail}
      />

      {/* Editor modal — shared for Create (tab-strip), Edit (card), Use (starter) */}
      <TemplateEditorModal
        open={isEditorOpen}
        initial={editing}
        defaultSubject={prefillSubject}
        defaultBody={prefillBody}
        onClose={handleEditorClose}
        onSaved={handleSaved}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        title="Delete template"
        message={`Remove "${deleteTarget?.subject || deleteTarget?.name || ''}"? Compose will no longer list it.`}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
        requireTypedConfirm={deleteTarget?.subject || deleteTarget?.name}
      />
    </div>
  );
}
